"use client";

import React from "react";
import { Server, ShieldCheck, Cctv, Mail, Globe, BellRing, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Υποστήριξη δικτύων & διακομιστών",
  desc: "Σχεδιασμός, υλοποίηση και συντήρηση υποδομών δικτύου υψηλής διαθεσιμότητας και ασφαλών διακομιστών για αδιάλειπτη λειτουργία.",
  icon: Server,
  className: "md:col-span-2 lg:col-span-2 lg:row-span-2",
  iconSize: "size-20"
}, {
  title: "Ρυθμίσεις πρόσβασης & ασφαλείας",
  desc: "Εφαρμογή αυστηρών πολιτικών ελέγχου πρόσβασης και προηγμένων πρωτοκόλλων κυβερνοασφάλειας.",
  icon: ShieldCheck,
  className: "md:col-span-1 lg:col-span-1",
  iconSize: "size-12"
}, {
  title: "Κάμερες & συστήματα ασφαλείας",
  desc: "Ολοκληρωμένα συστήματα παρακολούθησης CCTV με δυνατότητα απομακρυσμένης διαχείρισης.",
  icon: Cctv,
  className: "md:col-span-1 lg:col-span-1",
  iconSize: "size-12"
}, {
  title: "Διαχείριση email & hosting",
  desc: "Ασφαλής φιλοξενία εταιρικών δεδομένων και διαχείριση κρυπτογραφημένων επικοινωνιών.",
  icon: Mail,
  className: "md:col-span-1 lg:col-span-1",
  iconSize: "size-12"
}, {
  title: "Κατασκευή & συντήρηση ιστοσελίδων",
  desc: "Ανάπτυξη σύγχρονων, ασφαλών διαδικτυακών τόπων με έμφαση στην ταχύτητα και προστασία.",
  icon: Globe,
  className: "md:col-span-1 lg:col-span-1",
  iconSize: "size-12"
}, {
  title: "Εγκατάσταση συστημάτων συναγερμών",
  desc: "Έξυπνα συστήματα συναγερμού με άμεση ειδοποίηση και πλήρη ενσωμάτωση στο κεντρικό σύστημα ελέγχου της εγκατάστασης.",
  icon: BellRing,
  className: "md:col-span-2 lg:col-span-4",
  iconSize: "size-16"
}];
export default function IntegratedTechnologySolutions() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="ολοκληρωμένες-λύσεις-τεχνολογίας" className="dark bg-background text-foreground relative overflow-hidden py-24 px-4 sm:px-6 lg:px-8">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-10 pointer-events-none" />
      
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Ολοκληρωμένες Λύσεις <span className="text-primary">Τεχνολογίας</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Προηγμένες υποδομές πληροφορικής και συστήματα ασφαλείας, σχεδιασμένα για την απρόσκοπτη και ασφαλή λειτουργία της επιχείρησής σας.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 transition-all duration-1000 delay-200 transform ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {services.map((service, index) => {
          const Icon = service.icon;
          return <Card key={index} data-index={index} className={`group relative overflow-hidden bg-card/40 backdrop-blur-md border-primary/20 hover:border-primary/50 transition-colors duration-500 ${service.className}`}>
                {}
                <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl z-0">
                  <div className="absolute left-0 right-0 h-[2px] bg-primary/60 shadow-[0_0_20px_4px_var(--color-primary)] top-[-20%] group-hover:top-[120%] transition-all duration-[1.5s] ease-in-out opacity-0 group-hover:opacity-100" />
                </div>

                <CardContent className="p-8 flex flex-col h-full justify-center items-center text-center relative z-10">
                  <div className="mb-6 p-4 rounded-full bg-primary/10 text-primary group-hover:scale-110 group-hover:bg-primary/20 transition-all duration-500">
                    <Icon className={service.iconSize} strokeWidth={1.5} />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    {service.title}
                  </h3>
                  
                  <p className="text-muted-foreground text-sm line-clamp-2 mb-6 max-w-[90%]">
                    {service.desc}
                  </p>
                  
                  <div className="mt-auto">
                    <Button variant="link" asChild className="p-0 h-auto text-primary hover:text-primary-foreground group/btn">
                      <Link to="/ServicesPage" className="inline-flex items-center gap-2 text-sm font-medium">
                        Μάθετε περισσότερα 
                        <ArrowRight className="size-4 transition-transform group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}